import { useState } from 'react';
import type { CSSProperties } from 'react';
import GradientBackground from '../components/GradientBackground';
import GlassCard from '../components/GlassCard';
import NavBar from '../components/NavBar';

export type AuthenticatedHomeProps = {
  user: { email?: string; name?: string };
  onViewProfile: () => void;
  onLogout: () => void;
  apiCheck: {
    isLoading: boolean;
    result?: { ok: true; subject: string; email?: string } | null;
    error?: {
      message: string;
      kind: 'UNAUTHORIZED' | 'FORBIDDEN' | 'NETWORK' | 'UNKNOWN';
    } | null;
    run: () => Promise<void>;
  };
};

type ApiState = 'idle' | 'loading' | 'success' | 'error';


interface ActionCardProps {
  icon: string;
  label: string;
  sublabel: string;
  accentColor: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function ActionCard({ icon, label, sublabel, accentColor, onClick, loading, disabled }: ActionCardProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      style={{
        ...actionCardStyle,
        ...(hovered && !disabled ? { ...actionCardHoverStyle, borderColor: `${accentColor}40` } : {}),
        ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
      }}
    >
      <div style={{ ...actionIconCircleStyle, background: `${accentColor}20`, border: `1px solid ${accentColor}35` }}>
        <span style={{ fontSize: '22px' }}>{loading ? '⏳' : icon}</span>
      </div>
      <p style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', margin: 0 }}>
        {loading ? 'Calling API…' : label}
      </p>
      <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: 0, lineHeight: 1.4 }}>
        {sublabel}
      </p>
    </button>
  );
}

function ApiResultPanel({ apiState, result, error, onLogout }: {
  apiState: ApiState;
  result?: { ok: true; subject: string; email?: string } | null;
  error?: { message: string; kind: string } | null;
  onLogout: () => void;
}) {

  if (apiState === 'idle') {
    return (
      <div style={resultIdleStyle}>
        <span style={{ fontSize: '28px', marginBottom: '10px' }}>🔌</span>
        <p style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>
          No API call made yet
        </p>
        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: 1.6 }}>
          Click <strong style={{ color: 'rgba(255,255,255,0.5)' }}>Test API</strong> above to send a request
          <br />
          to <code style={inlineCodeStyle}>GET /auth-check</code> with your JWT.
        </p>
      </div>
    );
  }

  if (apiState === 'loading') {
    return (
      <div style={resultIdleStyle}>
        <div style={spinnerStyle} />
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '16px' }}>
          Sending request…
        </p>
      </div>
    );
  }

  const isSuccess = apiState === 'success';
  const borderColor = isSuccess ? 'rgba(74,222,128,0.25)' : 'rgba(239,68,68,0.25)';
  const bgColor = isSuccess ? 'rgba(74,222,128,0.06)' : 'rgba(239,68,68,0.06)';
  const statusColor = isSuccess ? '#4ade80' : '#f87171';

  const displayData = isSuccess && result
    ? { ok: true, sub: result.subject, email: result.email, statusCode: 200 }
    : { ok: false, error: error?.message ?? 'Unauthorized', statusCode: error?.kind === 'FORBIDDEN' ? 403 : 401 };

  return (
    <div style={{ animation: 'scale-in 0.3s cubic-bezier(0.16,1,0.3,1) both' }}>
      <div style={{ ...resultStatusBarStyle, background: bgColor, border: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>{isSuccess ? '✅' : '❌'}</span>
          <div>
            <p style={{ fontSize: '13px', fontWeight: 700, color: statusColor, margin: 0 }}>
              {isSuccess ? '200 OK — Authenticated' : `${displayData.statusCode} Unauthorized`}
            </p>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
              GET /auth-check · {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
        {!isSuccess && (
          <button
            onClick={onLogout}
            style={reloginBtnStyle}
          >
            Re-login
          </button>
        )}
      </div>
      <div style={jsonViewerStyle}>
        <pre style={jsonPreStyle}>{JSON.stringify(displayData, null, 2)}</pre>
      </div>
    </div>
  );
}

export default function AuthenticatedHome({ user, onViewProfile, onLogout, apiCheck }: AuthenticatedHomeProps) {
  const [apiState, setApiState] = useState<ApiState>('idle');

  const userName = user.name ?? user.email ?? 'User';
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleTestApi = async () => {
    setApiState('loading');
    await apiCheck.run();
    setApiState(apiCheck.error ? 'error' : 'success');
  };

  // Sync state with apiCheck props
  const displayState: ApiState =
    apiCheck.isLoading ? 'loading'
    : apiCheck.result ? 'success'
    : apiCheck.error ? 'error'
    : apiState;

  return (
    <GradientBackground>
      <NavBar
        userName={userName}
        userEmail={user.email ?? ''}
        active="home"
        onLogout={onLogout}
      />

      <div style={layoutStyle}>
        {/* ---- Welcome card ---- */}
        <GlassCard elevated style={welcomeCardStyle} className="anim-fade-in-up">
          <div style={welcomeLeftStyle}>
            <div style={bigAvatarStyle}>{initials}</div>
            <div>
              <div style={welcomeTagStyle}>
                <div style={onlineDotStyle} />
                <span style={{ fontSize: '11px', color: '#4ade80', fontWeight: 600, letterSpacing: '0.04em' }}>
                  SIGNED IN
                </span>
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '6px 0 4px' }}>
                Welcome back, {userName.split(' ')[0]}
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', margin: 0 }}>
                {user.email}
              </p>
            </div>
          </div>

        </GlassCard>

        {/* ---- Action cards row ---- */}
        <div className="anim-fade-in-up delay-200" style={actionRowStyle}>
          <ActionCard
            icon="👤"
            label="View Profile"
            sublabel="ID token claims"
            accentColor="#a855f7"
            onClick={onViewProfile}
          />
          <ActionCard
            icon="⚡"
            label="Test API"
            sublabel="GET /auth-check"
            accentColor="#3b82f6"
            onClick={() => { void handleTestApi(); }}
            loading={displayState === 'loading'}
            disabled={displayState === 'loading'}
          />
          <ActionCard
            icon="🚪"
            label="Logout"
            sublabel="End session"
            accentColor="#ef4444"
            onClick={onLogout}
          />
        </div>

        {/* ---- API result section ---- */}
        <div className="anim-fade-in-up delay-300" style={apiSectionStyle}>
          <GlassCard style={{ padding: '28px 32px' }}>
            <div style={apiSectionHeaderStyle}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#fff', margin: '0 0 2px' }}>
                  API Response
                </h3>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
                  Protected endpoint: <code style={inlineCodeStyle}>GET /auth-check</code>
                </p>
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <ApiResultPanel
                apiState={displayState}
                result={apiCheck.result}
                error={apiCheck.error}
                onLogout={onLogout}
              />
            </div>
          </GlassCard>
        </div>

        {/* ---- Tip bar ---- */}
        <div className="anim-fade-in delay-500" style={tipBarStyle}>
          <span style={{ fontSize: '13px' }}>💡</span>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
            Your ID token is sent as <code style={inlineCodeStyle}>Authorization: Bearer &lt;token&gt;</code> on every API call.
            API Gateway validates it against Cognito's JWKS endpoint.
          </span>
        </div>
      </div>
    </GradientBackground>
  );
}

/* ================================================================== */
/*  Styles                                                              */
/* ================================================================== */

const layoutStyle: CSSProperties = {
  paddingTop: '76px',
  paddingBottom: '48px',
  paddingLeft: '24px',
  paddingRight: '24px',
  maxWidth: '900px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const welcomeCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '24px',
  padding: '32px 36px',
};

const welcomeLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
};

const bigAvatarStyle: CSSProperties = {
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #a855f7, #6366f1, #3b82f6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '22px',
  fontWeight: 800,
  color: '#fff',
  flexShrink: 0,
  boxShadow: '0 0 0 3px rgba(168,85,247,0.25), 0 4px 16px rgba(0,0,0,0.4)',
};

const welcomeTagStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(74,222,128,0.1)',
  border: '1px solid rgba(74,222,128,0.25)',
  borderRadius: '20px',
  padding: '3px 10px',
};

const onlineDotStyle: CSSProperties = {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: '#4ade80',
  boxShadow: '0 0 6px #4ade80',
};


const actionRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
};

const actionCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  padding: '28px 20px',
  borderRadius: '18px',
  background: 'rgba(255,255,255,0.055)',
  border: '1px solid rgba(255,255,255,0.1)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  textAlign: 'center',
  boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
};

const actionCardHoverStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.09)',
  transform: 'translateY(-3px)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
};

const actionIconCircleStyle: CSSProperties = {
  width: '52px',
  height: '52px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '4px',
};

const apiSectionStyle: CSSProperties = {
  width: '100%',
};

const apiSectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '12px',
};

const resultIdleStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 20px',
  gap: '8px',
};

const spinnerStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  border: '3px solid rgba(59,130,246,0.15)',
  borderTopColor: '#3b82f6',
  animation: 'spin 0.9s linear infinite',
};

const resultStatusBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '12px',
  padding: '14px 18px',
  marginBottom: '12px',
};

const reloginBtnStyle: CSSProperties = {
  background: 'rgba(239,68,68,0.15)',
  border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: '8px',
  padding: '7px 14px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#fca5a5',
  cursor: 'pointer',
};

const jsonViewerStyle: CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '10px',
  padding: '16px 18px',
  maxHeight: '200px',
  overflowY: 'auto',
};

const jsonPreStyle: CSSProperties = {
  fontFamily: "'SF Mono', 'Fira Code', monospace",
  fontSize: '12px',
  lineHeight: 1.6,
  color: 'rgba(134,239,172,0.9)',
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

const inlineCodeStyle: CSSProperties = {
  fontFamily: 'monospace',
  background: 'rgba(168,85,247,0.15)',
  color: 'rgba(196,148,255,0.9)',
  padding: '1px 5px',
  borderRadius: '4px',
  fontSize: '11px',
};

const tipBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '12px',
  padding: '14px 18px',
  lineHeight: 1.6,
};
